import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const statusColors = {
  'Active': 'bg-blue-100 text-blue-700',
  'On Hold': 'bg-amber-100 text-amber-700',
  'Completed': 'bg-green-100 text-green-700',
  'Cancelled': 'bg-red-100 text-red-700',
  'Planning': 'bg-purple-100 text-purple-700',
};

const budgetColors = {
  on: 'bg-green-100 text-green-700',
  over: 'bg-red-100 text-red-700',
  under: 'bg-blue-100 text-blue-700',
};

const getBudgetStatus = (project) => {
  const budget = parseFloat(project?.budget ?? project?.contractValue ?? 0);
  const actual = parseFloat(project?.actualCost ?? 0);
  if (!budget || budget === 0) return 'on';
  const variance = ((actual - budget) / budget) * 100;
  if (variance > 5) return 'over';
  if (variance < -5) return 'under';
  return 'on';
};

const AssignedProjectsTable = ({ projects, loading }) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sorted = [...(projects ?? [])]?.sort((a, b) => {
    let aVal = a?.[sortField] ?? '';
    let bVal = b?.[sortField] ?? '';
    if (typeof aVal === 'string') aVal = aVal?.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal?.toLowerCase();
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ field }) => (
    <Icon
      name={sortField === field ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={14}
      className="ml-1 inline-block text-gray-400"
    />
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="animate-pulse space-y-3">
          {[1,2,3]?.map(i => <div key={i} className="h-10 bg-gray-100 rounded" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <Icon name="Briefcase" size={16} className="text-blue-600" />
          Assigned Projects
        </h3>
        <span className="text-xs text-gray-400">{sorted?.length} project{sorted?.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                Project <SortIcon field="name" />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                Status <SortIcon field="status" />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Progress
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Budget
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('endDate')}>
                End Date <SortIcon field="endDate" />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted?.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  No projects assigned
                </td>
              </tr>
            ) : (
              sorted?.map((project) => {
                const budgetStatus = getBudgetStatus(project);
                const progress = Math.min(parseFloat(project?.completionPercentage ?? project?.progress ?? 0), 100);
                return (
                  <tr key={project?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{project?.name}</div>
                      <div className="text-xs text-gray-400">{project?.projectNumber}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors?.[project?.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {project?.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5 min-w-16">
                          <div
                            className="h-1.5 rounded-full bg-blue-500"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-8">{progress}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${budgetColors?.[budgetStatus]}`}>
                        {budgetStatus === 'on' ? 'On Budget' : budgetStatus === 'over' ? 'Over Budget' : 'Under Budget'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {project?.endDate ? new Date(project.endDate)?.toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/project-detail-management/${project?.id}`)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center gap-1"
                      >
                        <Icon name="ExternalLink" size={12} />
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AssignedProjectsTable;
